import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { AUTH_TOKEN, USER_DATA } from 'constants/AuthConstant'
import AuthService from 'services/AuthService'

export const initialState = {
  loading: false,
  message: '',
  showMessage: false,
  redirect: '',
  token: localStorage.getItem(AUTH_TOKEN) || null,
  isPasswordChanged: false, // 패스워드 변경 여부
  isAlreadyLoggedIn: false, // 중복 로그인 여부
  isAccountStatusChanged: false, // 유저상태 변경 여부
  isLoginInfo: false,
}

const errorMessage =
  'ID와 암호를 확인하세요.\n담당자에게 연락하세요.\n\nCheck your ID and Password.\nContact system manager.'

export const signIn = createAsyncThunk('login', async (data, { rejectWithValue, dispatch }) => {
  const { username, password } = data

  try {
    const response = await AuthService.login({ username, password })

    if (response) {
      const userData = response.userData
      const token = response.token

      localStorage.setItem(AUTH_TOKEN, token)
      localStorage.setItem(USER_DATA, JSON.stringify(userData))

      dispatch(setIsLoginInfo(true))

      return token
    } else {
      return rejectWithValue(errorMessage)
    }
  } catch (error) {
    if (error.status === 702) {
      dispatch(setIsPasswordChanged(true))
      return rejectWithValue(error?.message)
    }

    return rejectWithValue(errorMessage)
  }
})

export const signOut = createAsyncThunk('logout', async (_, { dispatch }) => {
  await AuthService.logout()

  localStorage.removeItem(AUTH_TOKEN)
  localStorage.removeItem(USER_DATA)

  // 상태 업데이트
  dispatch(setIsAlreadyLoggedIn(false))
  dispatch(setIsAccountStatusChanged(false))
  dispatch(hideAuthMessage())

  history.push('/')

  return true
})

export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    authenticated: (state, action) => {
      state.loading = false
      state.redirect = '/'
      state.token = action.payload
    },
    showAuthMessage: (state, action) => {
      state.message = action.payload
      state.showMessage = true
      state.loading = false
    },
    hideAuthMessage: state => {
      state.message = ''
      state.showMessage = false
    },
    signOutSuccess: state => {
      state.loading = false
      state.token = null
      state.redirect = '/'
    },
    showLoading: state => {
      state.loading = true
    },
    hideLoading: state => {
      state.loading = false
    },
    signInSuccess: (state, action) => {
      state.loading = false
      state.token = action.payload
    },
    setIsPasswordChanged: (state, action) => {
      state.isPasswordChanged = action.payload
    },
    setMessage: (state, action) => {
      state.message = action.payload
    },
    setIsAlreadyLoggedIn: (state, action) => {
      state.isAlreadyLoggedIn = action.payload
    },
    setIsAccountStatusChanged: (state, action) => {
      state.isAccountStatusChanged = action.payload
    },
    setIsLoginInfo: (state, action) => {
      state.isLoginInfo = action.payload
    },
  },
  extraReducers: builder => {
    builder
      .addCase(signIn.pending, state => {
        state.loading = true
      })
      .addCase(signIn.fulfilled, (state, action) => {
        state.loading = false
        state.redirect = '/'
        state.token = action.payload
      })
      .addCase(signIn.rejected, (state, action) => {
        state.message = action.payload
        state.showMessage = true
        state.loading = false
      })
      .addCase(signOut.fulfilled, state => {
        state.loading = false
        state.token = null
        state.redirect = '/'
      })
      .addCase(signOut.rejected, state => {
        state.loading = false
        state.token = null
        state.redirect = '/'
      })
  },
})

export const {
  authenticated,
  showAuthMessage,
  hideAuthMessage,
  signOutSuccess,
  showLoading,
  hideLoading,
  signInSuccess,
  setIsPasswordChanged,
  setMessage,
  setIsAlreadyLoggedIn,
  setIsAccountStatusChanged,
  setIsLoginInfo,
} = authSlice.actions

export default authSlice.reducer
